import { create } from "zustand";
import { api, setAccessToken, ApiError } from "../lib/api";

type User = {
  id: string;
  email: string;
  displayName: string;
  role: "admin" | "teacher";
};

type AuthState = {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithMagicLink: (email: string) => Promise<string>;
  setPasswordFromInvitation: (
    password: string,
    passwordConfirm: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  setUserFromToken: (token: string, user: User) => void;
  clearError: () => void;
};

let initPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  error: null,

  initialize: async () => {
    if (get().isInitialized) return;

    if (!initPromise) {
      initPromise = (async () => {
        try {
          const data = await api<{ accessToken: string; user: User }>(
            "/api/auth/refresh",
            { method: "POST", skipAuth: true },
          );
          setAccessToken(data.accessToken);
          set({
            user: data.user,
            isAuthenticated: true,
            isInitialized: true,
          });
        } catch {
          setAccessToken(null);
          set({ user: null, isAuthenticated: false, isInitialized: true });
        }
      })();
    }

    await initPromise;
    initPromise = null;
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api<{ accessToken: string; user: User }>(
        "/api/auth/login",
        {
          method: "POST",
          body: JSON.stringify({ email, password }),
          skipAuth: true,
        },
      );
      setAccessToken(data.accessToken);
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Une erreur est survenue.";
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  loginWithMagicLink: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api<{ message: string }>("/api/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({ email }),
        skipAuth: true,
      });
      set({ isLoading: false });
      return data.message;
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Une erreur est survenue.";
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  setPasswordFromInvitation: async (password, passwordConfirm) => {
    set({ isLoading: true, error: null });
    try {
      await api("/api/auth/set-password", {
        method: "POST",
        body: JSON.stringify({ password, passwordConfirm }),
      });
      set({ isLoading: false });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Une erreur est survenue.";
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // Ignorer les erreurs de logout
    }
    setAccessToken(null);
    set({ user: null, isAuthenticated: false });
  },

  refreshToken: async () => {
    try {
      const data = await api<{ accessToken: string; user: User }>(
        "/api/auth/refresh",
        { method: "POST", skipAuth: true },
      );
      setAccessToken(data.accessToken);
      set({ user: data.user, isAuthenticated: true });
      return true;
    } catch {
      setAccessToken(null);
      set({ user: null, isAuthenticated: false });
      return false;
    }
  },

  setUserFromToken: (token, user) => {
    setAccessToken(token);
    set({ user, isAuthenticated: true });
  },

  clearError: () => {
    set({ error: null });
  },
}));
